import React, { useState } from 'react';

import { makeStyles, Theme } from '@material-ui/core/styles';
import clsx from 'clsx';
// eslint-disable-next-line import/named
import { getTranslate, TranslateFunction } from 'react-localize-redux';
import { connect } from 'react-redux';

import * as Actions from '../../../weborder/actions/Actions';
import * as Api from '../../../weborder/Api';
import * as Data from '../../../weborder/Data';
import { getAppConfig } from '../../../weborder/selectors/basketSelectors';
import * as Store from '../../../weborder/Store';
import { Button, Typography } from '../../atoms';
import { Dialog } from '../../molecules';

interface OuterProps {
  customer: Data.Async<Api.Customer>;
}
interface DispatchProps {
  SetConsentPermission: (consent: boolean) => any;
}
interface StateProps {
  privacyLink: string;
  termsLink: string;
  translate: TranslateFunction;
}

type Props = OuterProps & StateProps & DispatchProps;

const useStyles = makeStyles((theme: Theme) => ({
  campaignConsentDialog: {
    '& .MuiDialogTitle-root': {
      padding: theme.spacing(0, 0, 7),
    },
    '& .MuiTypography-h1': {
      paddingBottom: theme.spacing(1.75),
      [theme.breakpoints.up('sm')]: {
        paddingBottom: theme.spacing(2.5),
      },
    },
    '& .MuiDialogActions-root': {
      flexDirection: 'column',

      '& .MuiButtonBase-root': {
        '&:first-of-type': {
          marginBottom: theme.spacing(1),
        },

        '&:last-child': {
          marginLeft: 0,
        },
      },
    },
  },
}));

function CampaignConsentDialog(props: Props): JSX.Element {
  const {
    customer,
    privacyLink,
    SetConsentPermission: setConsentPermission,
    translate,
  } = props;

  const classes = useStyles();
  const [isShowDialog, setIsShowDialog] = useState(null);

  const handleOnConfirm = (): void => {
    setConsentPermission(true);
    setIsShowDialog(false);
  };
  const handleOnDeny = (): void => {
    setConsentPermission(false);
    setIsShowDialog(false);
  };

  const checkIsShowDialog = (): boolean => {
    if (isShowDialog === null) {
      if (customer.status !== 'success' || customer.data.IsGuest) {
        return false;
      }

      return customer.data.ConsentRequest.RequestConsent;
    } else {
      return isShowDialog;
    }
  };

  const getTextCopy = (part: string): string => {
    if (customer.status !== 'success' || customer.data.IsGuest) {
      return '';
    }

    switch (part) {
      case 'MessageText':
        return customer.data.ConsentRequest.MessageText;
      case 'ConfirmButtonText':
        return customer.data.ConsentRequest.ConfirmButtonText;
      case 'DenyButtonText':
        return customer.data.ConsentRequest.DenyButtonText;
    }
  };

  const campaignConsentDialogStyles = clsx(
    'fd-partial__campaign-consent',
    classes.campaignConsentDialog,
  );

  const dialogActions = () => {
    return (
      <>
        <Button
          aria-label={`${translate('Yes_option_in')}`}
          data-fd="permission-confirm"
          fullWidth
          key="confirm-button"
          onClick={handleOnConfirm}
        >
          {getTextCopy('ConfirmButtonText')}
        </Button>

        <Button
          aria-label={`${translate('No_option_out')}`}
          data-fd="permission-deny"
          fullWidth
          key="deny-button"
          onClick={handleOnDeny}
        >
          {getTextCopy('DenyButtonText')}
        </Button>
      </>
    );
  };

  return (
    <Dialog
      actions={dialogActions()}
      className={campaignConsentDialogStyles}
      disableBackdropClick={true}
      disableEscapeKeyDown={true}
      open={checkIsShowDialog()}
    >
      <Typography variant="h1">{getTextCopy('MessageText')}</Typography>
      <Typography variant="caption">
        {translate('By_opting_in_you_acknowledge_our')}{' '}
        <a href={privacyLink} rel="noopener noreferrer" target="_blank">
          {translate('Privacy_policy')}
        </a>
        .
      </Typography>
    </Dialog>
  );
}

const mapStateToProps = (state: Store.State): StateProps => {
  const config = getAppConfig(state);
  const getConfig = (param) => (config[param] ? config[param] : '');

  return {
    privacyLink: getConfig('PrivacyPolicyUrl'),
    termsLink: getConfig('TermsAndConditionsUrl'),
    translate: getTranslate(state.locale),
  };
};

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  SetConsentPermission: (consent: boolean) => {
    dispatch(Actions.SetConsentPermission(consent));
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(CampaignConsentDialog);
export { CampaignConsentDialog };
